import { useEffect, useState, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { BGrainOverlay, BStripes, BHeader } from '../components/layout';
import { BReportHero } from '../components/report/BReportHero';
import { BDiscoverSection } from '../components/report/BDiscoverSection';
import { BDeepDive, type TabId } from '../components/report/BDeepDive';
import { BShareModal } from '../components/modals/BShareModal';
import { BLoadingScreen } from '../components/loading/BLoadingScreen';
import { BFloatingStatus } from '../components/loading/BFloatingStatus';
import { getAuthHeaders, getOdotId } from '../../utils/odotId';
import type { Report } from '@tastymusic/research-agent';
import './BResultsPage.css';

// Artist result from SSE stream
interface ArtistResult {
  name: string;
  genres: string[];
  era: string | null;
  location: string | null;
  matchReason: string;
  confidence: number;
  tracks: string[];
  relatedArtists: string[];
  links?: {
    bandcamp?: string;
    spotify?: string;
    youtube?: string;
  };
  youtubeVideoId?: string;
  bandcampEmbed?: {
    embedHtml: string;
    albumId?: string;
    title?: string;
    bandcampUrl?: string;
  };
}

// Genre context from API
interface GenreContext {
  description?: string;
  keyArtists?: Array<{
    name: string;
    track?: string;
    significance?: string;
    links?: {
      bandcamp?: string;
      spotify?: string;
      youtube?: string;
      youtubeVideoId?: string;
    };
  }>;
  youtubeQueries?: string[];
  genreTags?: string[];
}

interface QueryResult {
  result: {
    genre: string;
    subgenres?: string[];
    era?: string;
    location?: string;
    mood?: string[];
    characteristics?: string[];
    search_query?: string;
  };
  conversation?: Array<{ role: string; content: string }>;
  locationResolved?: {
    name: string;
    population?: number;
  };
}

type ResearchPhase = 'starting' | 'context' | 'searching' | 'deep_research' | 'report' | 'complete' | 'cached' | 'healing';

// Map section types to tab IDs for progressive building indicator
const sectionToTab: Record<string, TabId | undefined> = {
  'introduction': 'genre',
  'founders': 'genre',
  'scene': 'scenes',
  'mutation': 'mutations',
  'moreArtists': 'more',
  'infrastructure': 'infrastructure',
  'coda': 'coda',
  'quickReference': 'reference',
};

// Map section type to report key
const getSectionKey = (sectionType: string): string => {
  const mapping: Record<string, string> = {
    'header': 'title', // special case - merged directly
    'introduction': 'introduction',
    'founders': 'founders',
    'scene': 'scenes', // array
    'mutation': 'mutations', // array
    'moreArtists': 'moreArtists',
    'infrastructure': 'infrastructure',
    'coda': 'coda',
    'quickReference': 'quickReference',
  };
  return mapping[sectionType] || sectionType;
};

// Extract headline from status message like "Researching female trap..." -> "Female Trap"
function extractHeadline(message: string): string {
  const match = message.match(/^Researching\s+(.+?)\.{0,3}$/i);
  if (match) {
    return match[1]
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }
  return '';
}

export function BResultsPage() {
  const { reportId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isLiveResearch = location.pathname === '/research' || location.pathname === '/b/research';

  // State
  const [isLoading, setIsLoading] = useState(true);
  const [phase, setPhase] = useState<ResearchPhase>('starting');
  const [statusMessage, setStatusMessage] = useState('Understanding the genre');
  const [report, setReport] = useState<Report | null>(null);
  const [artists, setArtists] = useState<ArtistResult[]>([]);
  const [genreContext, setGenreContext] = useState<GenreContext | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [deepDiveExpanded, setDeepDiveExpanded] = useState(true);
  const [queryDisplay, setQueryDisplay] = useState('');
  const [locationResolved, setLocationResolved] = useState<{ name: string; population?: number } | null>(null);
  const [isHealing, setIsHealing] = useState(false);
  const [buildingTab, setBuildingTab] = useState<TabId | undefined>(undefined);
  const [liveReportId, setLiveReportId] = useState<string | null>(null);

  const eventSourceRef = useRef<EventSource | null>(null);
  const artistNamesRef = useRef<Set<string>>(new Set());
  const startTimeRef = useRef<number>(Date.now());

  // Determine if we have enough content to show the report page
  // Show loading screen until we have genre context OR at least one artist
  const hasContent = genreContext !== null || artists.length > 0;

  // Reset scroll position on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // For live research, start SSE stream
  useEffect(() => {
    if (!isLiveResearch) return;

    const queryResultStr = sessionStorage.getItem('b_query_result');

    if (!queryResultStr) {
      navigate('/');
      return;
    }

    const queryResult: QueryResult = JSON.parse(queryResultStr);
    // Use the resolved genre from query result, NOT the raw search text
    // The genre is what the AI resolved the query to, which is more meaningful
    setQueryDisplay(queryResult.result.genre || '');
    if (queryResult.locationResolved) {
      setLocationResolved(queryResult.locationResolved);
    }

    // Start research stream
    startResearchStream(queryResult);

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, [isLiveResearch, navigate]);

  // For cached reports, fetch from API
  useEffect(() => {
    if (isLiveResearch || !reportId) return;

    fetchCachedReport(reportId);
  }, [isLiveResearch, reportId]);

  const startResearchStream = async (queryResult: QueryResult) => {
    try {
      const res = await fetch('/api/v1/research', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(queryResult),
      });

      if (!res.ok) {
        throw new Error('Research request failed');
      }

      const reader = res.body?.getReader();
      if (!reader) {
        throw new Error('No response body');
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              handleSSEEvent(data);
            } catch (e) {
              // Ignore parse errors
            }
          }
        }
      }

      setPhase('complete');
      setIsLoading(false);
    } catch (err) {
      console.error('Research stream error:', err);
      setError('Research failed. Please try again.');
      setIsLoading(false);
    }
  };

  const handleSSEEvent = (event: any) => {
    switch (event.type) {
      case 'report-id':
        // Capture report ID as early as possible for share URLs
        if (event.reportId) {
          setLiveReportId(event.reportId);
        }
        // Get early title if available (not "Discovering artists...")
        if (event.title && event.title !== 'Discovering artists...') {
          setQueryDisplay(event.title);
        }
        break;

      case 'status':
        setStatusMessage(event.message || 'Processing...');
        if (event.phase) {
          setPhase(event.phase as ResearchPhase);
        }
        // Extract headline from "Researching X..." message
        if (event.phase === 'context' && event.message) {
          const extracted = extractHeadline(event.message);
          if (extracted) {
            setQueryDisplay(extracted);
          }
        }
        break;

      case 'context':
        setPhase('context');
        if (event.context) {
          setGenreContext(event.context);
        }
        break;

      case 'artist':
        // Deduplicate by name
        const artistName = event.artist?.name?.toLowerCase();
        if (artistName && !artistNamesRef.current.has(artistName)) {
          artistNamesRef.current.add(artistName);
          setArtists(prev => [...prev, event.artist]);
        }
        break;

      case 'section':
        // Progressive section received - update report incrementally
        {
          const sectionType = event.sectionType as string;
          const data = event.data as Record<string, unknown>;

          // Header section contains the derived title
          if (sectionType === 'header' && data?.title) {
            setQueryDisplay(data.title as string);
          }

          // Update building tab indicator based on section type
          const tabForSection = sectionToTab[sectionType];
          if (tabForSection) {
            setBuildingTab(tabForSection);
          }

          // Build report incrementally from section data
          setReport((prev: Report | null) => {
            // Header is special - it contains top-level props (title, subtitle, tagline)
            if (sectionType === 'header') {
              return { ...(prev || {}), ...data } as Report;
            }

            if (!prev) {
              // Initialize report with first section
              const key = getSectionKey(sectionType);
              if (sectionType === 'scene') {
                return { scenes: [data] } as unknown as Report;
              } else if (sectionType === 'mutation') {
                return { mutations: [data] } as unknown as Report;
              }
              return { [key]: data } as unknown as Report;
            }

            // Merge new section into existing report
            const key = getSectionKey(sectionType);
            if (sectionType === 'scene') {
              // Append to scenes array
              const scenes = (prev.scenes || []) as unknown[];
              return { ...prev, scenes: [...scenes, data] } as Report;
            } else if (sectionType === 'mutation') {
              // Append to mutations array
              const mutations = (prev.mutations || []) as unknown[];
              return { ...prev, mutations: [...mutations, data] } as Report;
            } else {
              return { ...prev, [key]: data } as Report;
            }
          });
        }
        break;

      case 'report':
        if (event.report) {
          setReport((prev: Report | null) => ({
            ...prev,
            ...event.report,
          } as Report));
        }
        break;

      case 'complete':
        setPhase('complete');
        setIsLoading(false);
        setBuildingTab(undefined); // Clear building indicator
        if (event.reportId) {
          setLiveReportId(event.reportId);
          // Update URL without navigation
          window.history.replaceState(null, '', `/report/${event.reportId}`);
        }
        break;

      case 'redirect':
        // Another request is generating same report
        if (event.reportId) {
          navigate(`/report/${event.reportId}`);
        }
        break;
    }
  };

  const fetchCachedReport = async (id: string) => {
    try {
      const res = await fetch(`/api/v1/research/report/${id}`, {
        headers: getAuthHeaders(),
      });

      if (!res.ok) {
        throw new Error('Report not found');
      }

      const data = await res.json();
      setReport(data.report);
      setArtists(data.discoveredArtists || data.artists || []);
      setQueryDisplay(data.queryDisplay || data.report?.title || '');
      if (data.genreContext) {
        setGenreContext(data.genreContext);
      }
      if (data.locationResolved) {
        setLocationResolved(data.locationResolved);
      }

      // Auto-heal incomplete or still-generating reports
      if (data.status === 'incomplete' || data.status === 'generating') {
        setPhase('healing');
        setIsLoading(false); // Show content while healing
        healReport(id);
      } else {
        setPhase('complete');
        setIsLoading(false);
      }
    } catch (err) {
      console.error('Failed to fetch report:', err);
      setError('Report not found');
      setIsLoading(false);
    }
  };

  const healReport = async (id: string) => {
    setIsHealing(true);
    setStatusMessage('Completing report...');

    try {
      const odotId = getOdotId();
      const res = await fetch(`/api/v1/research/report/${id}?heal=true&odotId=${odotId}`, {
        headers: getAuthHeaders(),
        credentials: 'include',
      });

      if (!res.ok) throw new Error('Healing request failed');

      const reader = res.body?.getReader();
      if (!reader) throw new Error('No response body');

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              handleHealingEvent(data);
            } catch (e) {
              console.error('Failed to parse SSE data:', e);
            }
          }
        }
      }
    } catch (err) {
      console.error('Healing error:', err);
    } finally {
      setIsHealing(false);
      setPhase('complete');
      setStatusMessage('');
    }
  };

  const handleHealingEvent = (event: any) => {
    switch (event.type) {
      case 'healing':
        setStatusMessage(event.message || 'Completing report...');
        break;

      case 'context':
        if (event.context) {
          setGenreContext(event.context);
        }
        break;

      case 'artist':
        if (event.artist) {
          const newArtist = { ...event.artist };
          if (event.youtubeVideoId) {
            newArtist.youtubeVideoId = event.youtubeVideoId;
          }
          if (event.bandcampEmbed) {
            newArtist.bandcampEmbed = event.bandcampEmbed;
          }
          // Deduplicate by name
          setArtists(prev => {
            if (prev.some(a => a.name.toLowerCase() === newArtist.name.toLowerCase())) {
              return prev;
            }
            return [...prev, newArtist];
          });
        }
        break;

      case 'section':
        const section = event.section;
        if (section) {
          // Update building tab indicator based on section type
          const healingTabForSection = sectionToTab[section.type as string];
          if (healingTabForSection) {
            setBuildingTab(healingTabForSection);
          }
          setReport(prev => {
            if (!prev) return prev;
            // Update the report with the new section data
            return {
              ...prev,
              [section.type]: section.data,
            };
          });
        }
        break;

      case 'complete':
        setPhase('complete');
        setIsHealing(false);
        setBuildingTab(undefined); // Clear building indicator
        break;
    }
  };

  // Show loading screen only until we have content (context or artists)
  // After that, show the report page progressively with floating status
  if (isLoading && isLiveResearch && !hasContent) {
    return (
      <BLoadingScreen
        query={queryDisplay}
        status={statusMessage}
        phase={phase}
      />
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="b-page b-page--dark b-results-page">
        <BStripes />
        <BGrainOverlay />
        <BHeader showBack showShare={false} />

        <div className="b-results-error">
          <h2>{error}</h2>
          <button onClick={() => navigate('/')} className="b-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Get report title and meta
  const title = report?.title || queryDisplay || 'Loading...';
  const subtitle = report?.subtitle || '';
  const description = report?.introduction?.content || '';
  // Combine genre tags from multiple sources
  const genres = report?.genreTags || genreContext?.genreTags || [];
  // Era and location come from founders section or scene context, not introduction
  const era = '';
  const locationName = locationResolved?.name || '';
  const keySound = '';

  // Determine if any "Go Deeper" tab has content (same logic as BDeepDive)
  const hasGenre = !!(report?.introduction?.content || report?.founders?.founders?.length || report?.founders?.parentGenres?.length);
  const hasScenes = !!(report?.scenes && report.scenes.length > 0);
  const hasMutations = !!(report?.mutations && report.mutations.length > 0);
  const hasMore = !!(report?.moreArtists?.artists?.length || artists.length > 6);
  const hasInfra = !!(report?.infrastructure && (report.infrastructure.labels?.length || report.infrastructure.radio?.length || report.infrastructure.festivals?.length));
  const hasCoda = !!report?.coda?.content;
  const hasReference = !!(report?.quickReference && report.quickReference.length > 0);
  const hasAnyDeepDiveTab = hasGenre || hasScenes || hasMutations || hasMore || hasInfra || hasCoda || hasReference;

  return (
    <div className="b-page b-results-page">
      <BStripes />
      <BGrainOverlay />

      <BHeader
        showBack
        showBookmark
        showShare
        onShare={() => setShowShareModal(true)}
      />

      {/* Hero Section */}
      <BReportHero
        title={title}
        subtitle={subtitle}
        description={description}
        genres={genres}
        era={era}
        location={locationName}
        keySound={keySound}
        onGoDeeper={hasAnyDeepDiveTab ? () => {
          setDeepDiveExpanded(true);
          setTimeout(() => {
            const element = document.getElementById('b-deep-content');
            if (element) {
              const headerHeight = 80; // Account for fixed header
              const elementPosition = element.getBoundingClientRect().top + window.scrollY;
              window.scrollTo({ top: elementPosition - headerHeight, behavior: 'smooth' });
            }
          }, 100);
        } : undefined}
      />

      {/* Discover Section */}
      <BDiscoverSection
        artists={artists}
        genrePanel={genreContext || undefined}
        isBuilding={phase !== 'complete'}
      />

      {/* Deep Dive Section */}
      <BDeepDive
        report={report}
        artists={artists}
        isBuilding={phase !== 'complete'}
        buildingTab={buildingTab}
        expanded={deepDiveExpanded}
        onExpandedChange={setDeepDiveExpanded}
      />

      {/* Footer */}
      <footer className="b-results-footer">
        <button
          className="b-btn"
          onClick={() => navigate('/')}
        >
          New Search
        </button>
      </footer>

      {/* Share Modal */}
      {showShareModal && (
        <BShareModal
          title={title}
          url={liveReportId || reportId
            ? `${window.location.origin}/report/${liveReportId || reportId}`
            : window.location.href}
          onClose={() => setShowShareModal(false)}
        />
      )}

      {/* Floating Status during live research or healing */}
      {(isLiveResearch || isHealing) && (
        <BFloatingStatus
          phase={isHealing ? 'healing' : phase}
          message={statusMessage}
          isActive={isLoading || isHealing}
          startTime={startTimeRef.current}
        />
      )}
    </div>
  );
}
